import { useCallback, useEffect, useRef, useState } from "react";
import { useJsApiLoader } from "@react-google-maps/api";
import { haversineDistanceKm } from "../../../shared/utils/geoUtils";
import {
  GOOGLE_MAPS_LIBRARIES,
  GOOGLE_MAPS_SCRIPT_ID,
} from "../../../shared/config/googleMapsConfig";

export interface LatLng {
  lat: number;
  lng: number;
}

export interface DirectionsResult {
  distanceKm: number;
  durationHours: number;
  loading: boolean;
  error: string | null;
  /** Whether the result came from Google Directions (true) or haversine fallback (false) */
  isDirections: boolean;
}

/**
 * Round lat/lng to 3 decimal places (~111m precision) for cache keys.
 */
const roundCoord = (n: number): number => Math.round(n * 1000) / 1000;

const cacheKey = (origin: LatLng, destination: LatLng): string =>
  `${roundCoord(origin.lat)},${roundCoord(origin.lng)}->${roundCoord(destination.lat)},${roundCoord(destination.lng)}`;

interface CachedResult {
  distanceKm: number;
  durationHours: number;
  isDirections: boolean;
}

const cache = new Map<string, CachedResult>();

/**
 * Hook that wraps Google Maps DirectionsService for driving distance + time.
 * Falls back to haversine + heuristic if Directions API fails.
 *
 * Usage:
 * ```ts
 * const { distanceKm, durationHours, loading, error, isDirections } =
 *   useDirectionsCalculation(origin, destination);
 * ```
 */
const useDirectionsCalculation = (
  origin: LatLng | null,
  destination: LatLng | null
): DirectionsResult => {
  const apiKey = (window as any).app?.config?.googleMapsApiKey || "";
  const { isLoaded } = useJsApiLoader({
    id: GOOGLE_MAPS_SCRIPT_ID,
    googleMapsApiKey: apiKey,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const [distanceKm, setDistanceKm] = useState(0);
  const [durationHours, setDurationHours] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDirections, setIsDirections] = useState(false);

  const directionsServiceRef = useRef<google.maps.DirectionsService | null>(null);

  const computeFallback = useCallback(
    (o: LatLng, d: LatLng): CachedResult => {
      const straightLine = haversineDistanceKm(o.lat, o.lng, d.lat, d.lng);
      // Road distance heuristic: ~1.3x straight-line
      const roadDistance = straightLine * 1.3;
      // Average speed heuristic: 70 km/h
      const hours = roadDistance / 70;
      return {
        distanceKm: Math.round(roadDistance * 10) / 10,
        durationHours: Math.round(hours * 100) / 100,
        isDirections: false,
      };
    },
    []
  );

  useEffect(() => {
    if (!origin || !destination) {
      setDistanceKm(0);
      setDurationHours(0);
      setError(null);
      setIsDirections(false);
      return;
    }

    // Check if both locations are valid (non-zero)
    if (origin.lat === 0 && origin.lng === 0) return;
    if (destination.lat === 0 && destination.lng === 0) return;

    const key = cacheKey(origin, destination);

    // Check cache first
    const cached = cache.get(key);
    if (cached) {
      setDistanceKm(cached.distanceKm);
      setDurationHours(cached.durationHours);
      setIsDirections(cached.isDirections);
      setLoading(false);
      setError(null);
      return;
    }

    // If Google Maps not loaded, use fallback
    if (!isLoaded) {
      const fallback = computeFallback(origin, destination);
      cache.set(key, fallback);
      setDistanceKm(fallback.distanceKm);
      setDurationHours(fallback.durationHours);
      setIsDirections(false);
      return;
    }

    // Use DirectionsService
    setLoading(true);
    setError(null);

    if (!directionsServiceRef.current) {
      directionsServiceRef.current = new google.maps.DirectionsService();
    }

    directionsServiceRef.current.route(
      {
        origin: { lat: origin.lat, lng: origin.lng },
        destination: { lat: destination.lat, lng: destination.lng },
        travelMode: google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (
          status === google.maps.DirectionsStatus.OK &&
          result?.routes?.[0]?.legs?.[0]
        ) {
          const leg = result.routes[0].legs[0];
          const km = (leg.distance?.value || 0) / 1000;
          const hours = (leg.duration?.value || 0) / 3600;

          const roundedKm = Math.round(km * 10) / 10;
          const roundedHours = Math.round(hours * 100) / 100;

          const entry: CachedResult = {
            distanceKm: roundedKm,
            durationHours: roundedHours,
            isDirections: true,
          };
          cache.set(key, entry);

          setDistanceKm(roundedKm);
          setDurationHours(roundedHours);
          setIsDirections(true);
        } else {
          // Fallback to haversine
          const fallback = computeFallback(origin, destination);
          cache.set(key, fallback);
          setDistanceKm(fallback.distanceKm);
          setDurationHours(fallback.durationHours);
          setIsDirections(false);
          setError(`Directions API: ${status}`);
        }

        setLoading(false);
      }
    );
  }, [origin, destination, isLoaded, computeFallback]);

  return { distanceKm, durationHours, loading, error, isDirections };
};

export default useDirectionsCalculation;
